import { TabsComponent } from "@datapay-by-datacom/design-system";
import type {
  CompanyConfigProfileInput,
  CompanyOptionsProfile,
  CompanySettingsProfile,
  PayGroupOptionsProfile,
  PayGroupSettingsProfile,
} from "../../generated/core-api";
import {
  GROUP_LABELS,
  GROUPS,
  enabledSections,
  type CsErrors,
  type OptionGroup,
  type PgNumberErrors,
} from "../types";
import { OptionsSection } from "./OptionsSection";
import s from "./CopySettingsProfile.module.css";

interface CopySettingsProfileProps {
  /** The full config profile (Company options + optional Pay Group options). */
  profile: CompanyConfigProfileInput;
  onChange: (next: CompanyConfigProfileInput) => void;
  /** Keep legacy tabs or show every provider in review-style editable cards. */
  presentation?: "tabs" | "review";
  /** Show the "escalation days must be > 0" validation hint. */
  escalationError?: string;
  /**
   * Validation errors for Pay Group number fields, keyed by
   * {@link PayGroupOptionsProfile} key (e.g. `"DaysAfterTerminationDate"`).
   * Computed by the parent form and threaded down so `OptionsSection` can
   * surface them on the relevant `pg-number` field.
   */
  pgNumberErrors?: PgNumberErrors;
  /**
   * Validation errors for Company Settings string fields, keyed by
   * {@link CompanySettingsProfile} string key (e.g.
   * `"SetDirectAccessAdditionalResourcesUrl"`). Computed by the parent form
   * and threaded down so `OptionsSection` can surface them on the relevant
   * `cs-string` field.
   */
  csErrors?: CsErrors;
  /** Default provider shown when the uncontrolled tab strip mounts. */
  defaultGroup?: OptionGroup;
  /** Changes force a safe remount when validation needs to reveal another tab. */
  tabResetKey?: number;
}

/**
 * "Copy settings profile" editor — provider-first in both presentations, with
 * one dedicated tab per provider. The redesign uses a review-style card inside
 * each tab. Every provider nests each enabled section from the shared
 * `OPTION_SECTIONS` registry (Company options + Pay Group options).
 *
 * Company options bind to `profile.CompanyOptions`; Pay Group options bind to
 * `profile.PayGroupOptions` (lazy-initialised to an empty profile on first
 * edit, so a `PayGroupOptions` key is only included in the outgoing payload
 * once the user touches that section).
 *
 * Un-rendered keys (the excluded booleans with no confirmed Christmas label)
 * are preserved on every edit: each `onChange` spreads the existing
 * sub-profile rather than rebuilding it from only the rendered fields.
 *
 * The legacy tab strip is **uncontrolled** — `selectedTabKey="datapay"` maps
 * to the DS `defaultSelectedKey` only (the DS API exposes no controlled
 * `selectedKey`).
 */
export function CopySettingsProfile({
  profile,
  onChange,
  presentation = "review",
  escalationError,
  pgNumberErrors,
  csErrors,
  defaultGroup = "datapay",
  tabResetKey = 0,
}: CopySettingsProfileProps) {
  const handleCompanyChange = (next: CompanyOptionsProfile) =>
    onChange({ ...profile, CompanyOptions: next });

  const handlePgChange = (next: PayGroupOptionsProfile) =>
    onChange({ ...profile, PayGroupOptions: next });

  const handleCsChange = (next: CompanySettingsProfile) =>
    onChange({ ...profile, CompanySettings: next });

  const handlePgsChange = (next: PayGroupSettingsProfile) =>
    onChange({ ...profile, PayGroupSettings: next });

  const sections = enabledSections();

  const renderSections = (
    group: (typeof GROUPS)[number],
    sectionPresentation: "legacy" | "review"
  ) => (
    <div className={s.sections}>
      {sections.map((section) => (
        <OptionsSection
          key={section.id}
          section={section}
          group={group}
          companyValues={profile.CompanyOptions}
          onCompanyChange={handleCompanyChange}
          pgValues={profile.PayGroupOptions}
          onPgChange={handlePgChange}
          csValues={profile.CompanySettings}
          onCsChange={handleCsChange}
          pgsValues={profile.PayGroupSettings}
          onPgsChange={handlePgsChange}
          escalationError={escalationError}
          pgNumberErrors={pgNumberErrors}
          csErrors={csErrors}
          presentation={sectionPresentation}
        />
      ))}
    </div>
  );

  if (presentation === "review") {
    const reviewTabs = GROUPS.map((group) => ({
      id: group,
      title: GROUP_LABELS[group].replace("Datapay", "DataPay"),
      panel: (
        <div className={s.providerCard}>
          {renderSections(group, "review")}
        </div>
      ),
    }));

    return (
      <div
        className={s.reviewEditor}
        aria-label="Company configuration options"
      >
        <TabsComponent
          key={`${defaultGroup}-${tabResetKey}`}
          tabs={reviewTabs}
          label="Company configuration providers"
          selectedTabKey={defaultGroup}
        />
      </div>
    );
  }

  const tabs = GROUPS.map((group) => ({
    id: group,
    title: GROUP_LABELS[group],
    panel: renderSections(group, "legacy"),
  }));

  return (
    <TabsComponent
      key={`${defaultGroup}-${tabResetKey}`}
      tabs={tabs}
      label="Company configuration options"
      selectedTabKey={defaultGroup}
    />
  );
}
